import * as THREE from "three";

import { ObjectHelper } from "./ObjectHelper";
import { App3D } from "./App3D";
import { Slots } from "./Slots";

export enum Axis {
	X, Y, Z
}

class OffsetDelta {
	public offset:number = 0;

	constructor(
		public axis:Axis,
		public sign:number){
		switch (axis){
			case Axis.Y: this.offset = 1; break;
			case Axis.Z: this.offset = 2; break;
		}
	}
}

export enum MeshAttributes{
	// must correspond with attributes used in shader-src/general.vert
	Offset = 'offset',
	// UVCorrection = 'uvCorrection',
	CorrectionU = 'vCorrectionU',
	CorrectionV = 'vCorrectionV',
}


export class ObjectLoader {
	private path: any = null;
	// +Z-V*.5
	protected ReDimensionProperty = /^ctl:([TLBR]|(?:[-+]?[XYZ]))(?:([-+][UV])(?:([\*\/])(\d*\.?\d+))?)?/;

	protected objectHelper: ObjectHelper;

	constructor(
		protected appContext: App3D,
		protected slotStorage: Slots = null
	) {
		this.objectHelper = new ObjectHelper();
	}

	setPath(path) {
		this.path = path;
	}

	load(url:string, onLoad, onProgress, onError) {
		let ldr = this.appContext.getLoader('json_ex');
		if (!ldr)
			throw 'Unsupported format';
		url = url.replace(/^\//, '');
		ldr.loader.load(this.path + url, (geometry) => {
			if (ldr.fixNormals)
				this.objectHelper.fixNormals(geometry);
			if (ldr.fixTransform)
				this.objectHelper.fixTransforms(geometry);
			if (ldr.mirrorUVs)
				this.objectHelper.mirrorUVs(geometry);
			if (ldr.createMesh)
				geometry = new THREE.Mesh(geometry);
			if (ldr.initScaling)
				this.initBufferProperties(geometry);

			// find all slots in currently loaded model
			let slots = ObjectHelper.processSlots(geometry);

console.debug(`Slots for [${url}]:`, slots);
			if (Object.keys(slots).length)
				if (this.slotStorage)
					this.slotStorage.append(slots);	// when loading product model, append it to slots listing
				else	// when loading part which has slot, but it is not a product, report warning
					console.warn('Loaded object [%s] containing slots with no slot storage!', url);

			geometry.position.set(0, 0, 0);

			geometry.traverse((mesh) => {
				if (mesh.isMesh)
					mesh.userData['material'] = mesh.material.name;
			});

			if (ldr.fixMaterials || ldr.replaceMaterial) {
				geometry.traverse((mesh) => {
					if (mesh.isMesh){

						let mm = ldr.replaceMaterial
							? ldr.replaceMaterial
							: this.appContext.getMaterial(mesh.material.name);
						if (mm) {
							// this.appContext.logger.log(mesh.name + ':' + mesh.material.name, 'material');
							mesh.material = mm;
						}

						// this.appContext.logger.log('Loaded: ' + mesh.name, 'load'); // $DEV
						mesh.normalsNeedUpdate = true;
						mesh.verticesNeedUpdate = true;
					}
				});
			}
			if (onLoad)
				onLoad(geometry);
		}, onProgress, onError);
	}

	/**
	 * Traverses through all childs and set-ups scaling-related attributes
	 * This takes [B+U]-like original attributes and transforms them to 'offset' and 'uvCorrection' attributes used by Vertex shader
	 * @param {Object3D} geometry
	 */
	protected initBufferProperties(geometry: THREE.Object3D){
		geometry.traverse((child: THREE.Object3D) => {
			// parse all meshes
			if (child instanceof THREE.Mesh){
				// Only Buffer geometry is supported (all data are stored to GPU RAM)
				if (child.geometry instanceof THREE.BufferGeometry){
					// We only need to initialize when there are some UV coordinates to update
					if (child.geometry.attributes.hasOwnProperty('uv2'))
						this.setupBufferAttributes(child.geometry);
					// else
						// console.info(`Mesh [${child.name}] has no secondary UV coordinates - skipping`);
				}
				else
					console.warn(`Mesh type of [${child.type}] is not supported: ${child.name}`);
			}
			else
				console.info(`Skipping non-geometry element [${child.type}] ${child.name}`);
		});
	}

	protected setupBufferAttributes(geometry:THREE.BufferGeometry){
		let vertexCount:number = geometry.attributes['position'].count;
		// UV supports only 1 axis for U and 1 axis for V direction. Multiple axes will cause unexpected behaviour
		// let vertexUVCorrection:Uint8Array = new Uint8Array(vertexCount);	// only WebGL2 and above

		// let vertexUVCorrection:Float32Array = new Float32Array(vertexCount);
		let vertexCorrectionU:Float32Array = new Float32Array(vertexCount * 3);
		let vertexCorrectionV:Float32Array = new Float32Array(vertexCount * 3);
		let vertexOffsets:Float32Array = new Float32Array(vertexCount * 3);
		/** Whether to use UV correction attribute for mesh */
		let useOffset = false;
		/** Whether to use UV correction attribute for mesh */
		let useCorrection = false;
		/** Attribute name RegExp match results */
		let grpParts:string[];
		/** List of original attributes to be removed */
		let attRemove:string[] = [];

		// Search for vertex groups named 'ctl:*' in geometry attributes
		for (let attName in geometry.attributes)
			if ((grpParts = attName.match(this.ReDimensionProperty)) && geometry.attributes.hasOwnProperty(attName)){
				attRemove.push(attName);	// remove this later, so it is not loaded to graphics memory

				let offset: OffsetDelta = null;
				// Get XYZ axis influenced by vertex group
				switch (grpParts[1]){ // get group 3D axis
					case 'L':
					case '-X':
						offset = new OffsetDelta(Axis.X, -1);
						break;
					case 'R':
					case '+X':
						offset = new OffsetDelta(Axis.X, 1);
						break;
					case 'B':
					case '-Z':
						offset = new OffsetDelta(Axis.Z, -1);
						break;
					case 'T':
					case '+Z':
						offset = new OffsetDelta(Axis.Z, 1);
						break;
					case '-Y':
						offset = new OffsetDelta(Axis.Y, -1);
						break;
					case '+Y':
						offset = new OffsetDelta(Axis.Y, 1);
						break;
						// automatic offset sign detection
					case 'X':
						offset = new OffsetDelta(Axis.X, 0);
						break;
					case 'Y':
						offset = new OffsetDelta(Axis.Y, 0);
						break;
					case 'Z':
						offset = new OffsetDelta(Axis.Z, 0);
						break;
				}

				// Vertex is movable (deforms object)
				if (offset){
					useOffset = true;
					// offset direction is defined by group name
					if (offset.sign)
						geometry.attributes[attName].array.forEach((val:number, v:number) => {
							// set corresponding vector component to group weight sign-ed with direction
							if (val > 0)
								vertexOffsets[3 * v + offset.offset] = val * offset.sign;
						});
					// offset is determined automatically by current vertex position
					else{
						let vertexPositions: THREE.BufferAttribute = geometry.attributes['position'];
						let vi:number;
						geometry.attributes[attName].array.forEach((val:number, v:number) => {
							if (val > 0){
								vi = 3 * v + offset.offset;
								if (vertexPositions.array[vi] < 0)
									val *= -1;
								vertexOffsets[vi] = val;
							}
						});
					}


					// Get UV axis influenced by vertex group
					/* Correction by flags
					let correctionDelta: CorrectionDelta = null;
					if (grpParts[2]){
						switch (grpParts[2]){
							case '-U':
								correctionDelta = new CorrectionDelta(offset.axis, UV.U, -1);
								break;
							case '+U':
								correctionDelta = new CorrectionDelta(offset.axis, UV.U, 1);
								break;
							case '-V':
								correctionDelta = new CorrectionDelta(offset.axis, UV.V, -1);
								break;
							case '+V':
								correctionDelta = new CorrectionDelta(offset.axis, UV.V, 1);
								break;
						}

						if (correctionDelta){
							useCorrection = true;
							// separate loop as it is much faster
							geometry.attributes[attName].array.forEach((val:number, v:number) => {
								vertexUVCorrection[v] |= correctionDelta.flags;
							});
						}
					}
					*/


					if (grpParts[2]){
						/* Correction by dedicated vertex attributes */
						/** Which buffer attribute is changes (either U or V) */
						let correctionAttribute:Float32Array = null;
						/** Whether is this positive influence (+X => +U) or negative (+X => -U) */
						let correctionSign = 0;
						let uvQuotient = 1;
						if (grpParts[4]){
							uvQuotient = Number(grpParts[4]);

							switch (grpParts[3]){
								case '/':
									if (uvQuotient > 0)
										uvQuotient = 1 / uvQuotient;
							}
						}
						switch (grpParts[2]){
							case '-U':
								correctionAttribute = vertexCorrectionU;
								correctionSign = -1;
								break;
							case '+U':
								correctionAttribute = vertexCorrectionU;
								correctionSign = 1;
								break;
							case '-V':
								correctionAttribute = vertexCorrectionV;
								correctionSign = -1;
								break;
							case '+V':
								correctionAttribute = vertexCorrectionV;
								correctionSign = 1;
								break;
						}
						if (correctionAttribute) {
							useCorrection = true;
							geometry.attributes[attName].array.forEach((val: number, v: number) => {
								if (val > 0)
									correctionAttribute[v * 3 + offset.axis] = correctionSign * val * uvQuotient;
							});
						}
					}
				}
			}

		if (useOffset){
			geometry.addAttribute(MeshAttributes.Offset, new THREE.BufferAttribute( vertexOffsets, 3 ) );
			if (useCorrection){
				// geometry.addAttribute(MeshAttributes.UVCorrection, new THREE.BufferAttribute( vertexUVCorrection, 1 ) );
				geometry.addAttribute(MeshAttributes.CorrectionU, new THREE.BufferAttribute( vertexCorrectionU, 3 ) );
				geometry.addAttribute(MeshAttributes.CorrectionV, new THREE.BufferAttribute( vertexCorrectionV, 3 ) );
			}
		}

		// remove original attributes as they are not needed
		for (let attName of attRemove)
			geometry.removeAttribute(attName);
	}
}

export class LoaderConfig{
	// loader: THREE.Loader = null; // compatibility with THREE.ObjectLoader is broken, so no type :(
	loader = null;
	fixNormals: boolean = false;
	fixMaterials: boolean = false;
	fixTransform: boolean = false;
	mirrorUVs: boolean = false;
	replaceMaterial = null;
	createMesh: boolean = false;
	initScaling: boolean = true;
}



// WEBPACK FOOTER //
// ./src/script/app3D/ObjectLoader.ts